import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { StravaConnectButton } from "../components/StravaConnectButton";
import { FarcasterAddButton } from "../components/FarcasterAddButton";
import { StravaAnalysisSection } from "../components/StravaAnalysisSection";
import { useStravaConnection } from "@/hooks/useStravaConnection";
import { useStravaConnectionResult } from "@/hooks/useStravaConnectionResult";
import { stravaApi } from "@/api/strava";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { MOCK_ANALYSIS } from "./AnalysisSummary";

export function ProfileSuccess() {
  const navigate = useNavigate();
  const { isConnected } = useStravaConnection();

  // Handle Strava OAuth callback result (shows toast, cleans URL, refreshes connection)
  useStravaConnectionResult();

  const handleDiscoverChallenges = () => {
    navigate("/");
  };

  const handleSkip = () => {
    navigate("/");
  };

  // DEV ONLY: Preview analysis screens with mock data
  const handleDevPreview = () => {
    navigate("/analysis-summary", { state: { mockData: MOCK_ANALYSIS } });
  };

  const handleReconnectStrava = async () => {
    try {
      const { authorizationUrl } = await stravaApi.getAuthorizationUrl("/profile-success");
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url: authorizationUrl });
      } else {
        window.location.href = authorizationUrl;
      }
    } catch (error) {
      console.error("Failed to reconnect Strava:", error);
    }
  };

  return (
    <div className="relative flex h-auto min-h-screen w-full flex-col group/design-root overflow-x-hidden p-6 bg-background-light dark:bg-background-dark font-display text-content-light dark:text-content-dark">
      {/* Sticky safe-area spacer */}
      <div className="sticky top-0 z-10 w-full h-[env(safe-area-inset-top)] bg-background-light dark:bg-background-dark" />
      <div className="flex flex-1 flex-col items-center justify-center text-center">
        {/* Success Icon with Ripple Animation */}
        <div className="relative flex h-32 w-32 items-center justify-center">
          {/* Ripple Animations */}
          <div className="absolute inset-0 z-0">
            <div className="h-full w-full rounded-full bg-primary/20 dark:bg-primary/30 animate-ripple-1"></div>
            <div className="absolute inset-0 h-full w-full rounded-full bg-primary/20 dark:bg-primary/30 animate-ripple-2"></div>
          </div>

          {/* Check Icon */}
          <div className="relative z-10 flex h-24 w-24 items-center justify-center rounded-full bg-primary/20 dark:bg-primary/30 animate-check-grow">
            <Check className="w-12 h-12 text-primary" strokeWidth={3} />
          </div>
        </div>

        {/* Success Message */}
        <div className="mt-8 space-y-4">
          <h1 className="text-4xl font-extrabold tracking-tight">
            You're All Set!
          </h1>
          <p className="max-w-sm text-base leading-relaxed text-content-light/80 dark:text-content-dark/80">
            Your profile is ready. Take the next step to supercharge your
            fitness journey.
          </p>
        </div>
      </div>

      {/* Action Cards */}
      <div className="w-full shrink-0 space-y-4 pt-8">
        <div className="space-y-4">
          {/* Strava Section - Show connect button or analysis */}
          {isConnected ? (
            <StravaAnalysisSection onReconnectStrava={handleReconnectStrava} />
          ) : (
            <StravaConnectButton />
          )}

          {/* Enable Farcaster Notifications */}
          <FarcasterAddButton />

          {/* Discover Challenges Card */}
          <button
            onClick={handleDiscoverChallenges}
            className="flex items-center gap-4 rounded-lg bg-card-light dark:bg-subtle-dark p-4 text-left shadow-sm ring-1 ring-subtle-light dark:ring-0 w-full hover:opacity-90 transition-opacity"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20">
              <span className="material-symbols-outlined text-primary">
                emoji_events
              </span>
            </div>
            <div className="flex-1">
              <p className="font-bold">Discover Challenges</p>
              <p className="text-sm text-content-light/70 dark:text-content-dark/70">
                Find your first challenge to join.
              </p>
            </div>
            <span className="material-symbols-outlined text-content-light/50 dark:text-content-dark/50">
              chevron_right
            </span>
          </button>
        </div>

        {/* Skip Button */}
        <div className="pt-4 text-center">
          <button
            onClick={handleSkip}
            className="h-10 cursor-pointer rounded-full bg-transparent px-4 text-sm font-bold text-content-light/70 dark:text-content-dark/70 hover:opacity-80 transition-opacity"
          >
            <span className="truncate">I'll do this later</span>
          </button>
        </div>

        {/* DEV ONLY: Preview Analysis Screens */}
        {import.meta.env.DEV && (
          <div className="pt-2 text-center">
            <button
              onClick={handleDevPreview}
              className="h-8 cursor-pointer rounded-full bg-yellow-500/20 border border-yellow-500/50 px-4 text-xs font-bold text-yellow-400 hover:bg-yellow-500/30 transition-colors"
            >
              🔧 DEV: Preview Analysis Screens
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
